/* components/ui/Select.jsx — кастомный select: поповер со списком, поиском и группами.
   Закрывается по Esc / клику-вне. На xs/sm (≤720px) — fullscreen-лист (bottom sheet)
   с полем поиска сверху. Разделяет логику позиционирования с Popover (баг z-index §15.2).
   Пропсы: value, onChange(value), options[{value,label,group?,hint?,icon?}],
           placeholder, searchable, disabled, invalid, width,
           multiple, bulkActions.
   ХЕНДОФФ → frontend/components/common/Select.jsx. */

import React from 'react';
import ReactDOM from 'react-dom';
import { Icon } from './Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { getOverlayRoot, overlay } from '../../lib/overlay.jsx';

  const { useState, useRef, useEffect, useLayoutEffect, useMemo } = React;
  const { useBreakpoint } = hooks;
  const { useFocusTrap } = overlay;

  function Select({ value, onChange, options = [], placeholder = 'Выберите…',
                    searchable = true, disabled = false, invalid = false, width, className, ariaLabel,
                    multiple = false, hideCaret = false, popoverClassName = '',
                    bulkActions = false }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const searchRef = useRef(null);
    const { isMobile } = useBreakpoint();

    const norm = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    const selectedVals = multiple ? (Array.isArray(value) ? value : []) : [];
    const isSel = (o) => (multiple ? selectedVals.includes(o.value) : o.value === value);
    const selected = multiple ? null : norm.find((o) => o.value === value);

    const filtered = useMemo(() => {
      if (!q) return norm;
      const s = q.toLowerCase();
      return norm.filter((o) => (o.label + ' ' + (o.hint || '')).toLowerCase().includes(s));
    }, [q, options]);

    const groups = useMemo(() => {
      const map = new Map();
      filtered.forEach((o) => {
        const g = o.group || '';
        if (!map.has(g)) map.set(g, []);
        map.get(g).push(o);
      });
      return [...map.entries()];
    }, [filtered]);

    const place = () => {
      const t = triggerRef.current; if (!t) return;
      const r = t.getBoundingClientRect();
      const w = width || r.width;
      const ph = panelRef.current ? panelRef.current.offsetHeight : 280;
      let top = r.bottom + 6;
      if (top + ph > window.innerHeight - 8) top = Math.max(8, r.top - ph - 6);
      let left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
      setPos({ top, left, width: w });
    };

    useLayoutEffect(() => { if (open && !isMobile) place(); /* eslint-disable-next-line */ }, [open, filtered.length]);
    useEffect(() => {
      if (!open) { setQ(''); return; }
      if (searchable) setTimeout(() => searchRef.current && searchRef.current.focus(), 0);
      const onDoc = (e) => {
        if (panelRef.current && panelRef.current.contains(e.target)) return;
        if (triggerRef.current && triggerRef.current.contains(e.target)) return;
        setOpen(false);
      };
      const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
      const reflow = () => !isMobile && place();
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', reflow, true);
      window.addEventListener('scroll', reflow, true);
      return () => {
        document.removeEventListener('mousedown', onDoc);
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', reflow, true);
        window.removeEventListener('scroll', reflow, true);
      };
    }, [open, isMobile]);

    // Мобильный bottom-sheet — модальный лист: ловушка Tab, возврат фокуса, Esc.
    // На десктопе (popover) трап не нужен — там фокус/Esc через document-эффект выше.
    useFocusTrap(panelRef, { active: open && isMobile, onEscape: () => setOpen(false) });

    const pick = (o) => {
      if (multiple) {
        const next = selectedVals.includes(o.value)
          ? selectedVals.filter((v) => v !== o.value)
          : [...selectedVals, o.value];
        onChange && onChange(next);          // мультивыбор — поповер НЕ закрываем
      } else {
        onChange && onChange(o.value);
        setOpen(false);
      }
    };

    // Массовые действия мультивыбора (bulkActions). Нужны там, где вариантов
    // ДЕСЯТКИ: у «Мезона» 109 групп блюд, и «выбрать всё, кроме трёх» означало
    // 106 кликов. «Выбрать все» уважает поиск — набрал «десерт», нажал, получил
    // все найденные; «Инвертировать» работает по ПОЛНОМУ списку (переворот
    // отфильтрованного подмножества читался бы как потеря выбора за пределами
    // фильтра).
    const selectAll = () => {
      const add = filtered.map((o) => o.value);
      onChange && onChange([...new Set([...selectedVals, ...add])]);
    };
    const invert = () => {
      onChange && onChange(norm.filter((o) => !selectedVals.includes(o.value)).map((o) => o.value));
    };
    const bulkBar = multiple && bulkActions && (
      <div className="select-pop__bulk">
        <button type="button" className="select-pop__bulk-btn" onClick={selectAll}
          title={q ? 'Выбрать все найденные по запросу' : 'Выбрать все варианты'}>
          <Icon name="check" size={13} /> {q ? `Выбрать найденные · ${filtered.length}` : 'Выбрать все'}
        </button>
        <button type="button" className="select-pop__bulk-btn" onClick={invert}
          title="Снять выбранные и выбрать остальные">
          <Icon name="refresh" size={13} /> Инвертировать
        </button>
        {selectedVals.length > 0 && (
          <button type="button" className="select-pop__bulk-btn" onClick={() => onChange && onChange([])}
            title="Снять весь выбор">
            <Icon name="close" size={13} /> Очистить · {selectedVals.length}
          </button>
        )}
      </div>
    );

    const list = (
      <div className="select-pop__list" role="listbox"
        aria-multiselectable={multiple || undefined} aria-label={ariaLabel || placeholder}>
        {bulkBar}
        {multiple && !bulkActions && selectedVals.length > 0 && (
          <button type="button" className="select-pop__clear" onClick={() => onChange && onChange([])}>
            <Icon name="close" size={14} /> Очистить ({selectedVals.length})
          </button>
        )}
        {groups.length === 0 || filtered.length === 0
          ? <div className="select-pop__empty t-body-sm text-mute">Ничего не найдено</div>
          : groups.map(([g, items]) => (
            <div className="select-pop__group" key={g || '_'}>
              {g && <div className="select-pop__group-label">{g}</div>}
              {items.map((o) => (
                <button type="button" key={o.value} role="option" aria-selected={isSel(o)}
                  className={'select-pop__opt' + (isSel(o) ? ' is-selected' : '')} onClick={() => pick(o)}>
                  {o.icon && <Icon name={o.icon} size={16} />}
                  <span className="grow">
                    <span className="select-pop__opt-label">{o.label}</span>
                    {o.hint && <span className="select-pop__opt-hint t-caption">{o.hint}</span>}
                  </span>
                  {isSel(o) && <Icon name="check" size={16} className="select-pop__tick" />}
                </button>
              ))}
            </div>
          ))}
      </div>
    );

    const search = searchable && (
      <div className="select-pop__search">
        <Icon name="search" size={15} />
        <input ref={searchRef} className="select-pop__search-input" value={q}
          onChange={(e) => setQ(e.target.value)} placeholder="Поиск…" />
      </div>
    );

    return (
      <>
        <button type="button" ref={triggerRef} disabled={disabled}
          className={'select-trigger' + (invalid ? ' input--error' : '') + (open ? ' is-open' : '') + (className ? ' ' + className : '')}
          style={width ? { width } : undefined}
          aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel}
          onClick={() => setOpen((o) => !o)}>
          {multiple ? (
            <span className={'select-trigger__value' + (selectedVals.length ? '' : ' is-placeholder')}>
              {selectedVals.length === 0
                ? placeholder
                : selectedVals.length === 1
                  ? ((norm.find((o) => o.value === selectedVals[0]) || {}).label || '1 выбрано')
                  : selectedVals.length + ' выбрано'}
            </span>
          ) : (
            <span className={'select-trigger__value' + (selected ? '' : ' is-placeholder')}>
              {selected ? (<>{selected.icon && <Icon name={selected.icon} size={16} />}{selected.label}</>) : placeholder}
            </span>
          )}
          {/* стрелка ВНИЗ (не «up-down»): двойная стрелка читалась как сортировка и рендерилась неровно */}
          {!hideCaret && <Icon name="chevron-down" size={15} className="select-trigger__caret" />}
        </button>

        {open && !isMobile && ReactDOM.createPortal(
          <div className={'select-pop' + (popoverClassName ? ' ' + popoverClassName : '')} ref={panelRef}
            style={{ top: pos.top, left: pos.left, width: pos.width, zIndex: 'var(--z-popover)' }}>
            {search}{list}
          </div>,
          getOverlayRoot()
        )}

        {open && isMobile && ReactDOM.createPortal(
          <div className="overlay overlay--sheet" style={{ zIndex: 'var(--z-popover)' }}>
            <div className="overlay__backdrop" onClick={() => setOpen(false)} />
            <div className={'select-sheet' + (popoverClassName ? ' ' + popoverClassName : '')} ref={panelRef} role="dialog" aria-modal="true">
              <div className="select-sheet__head">
                <span className="t-h4 grow">{placeholder}</span>
                <button type="button" className="modal__close" onClick={() => setOpen(false)} aria-label="Закрыть"><Icon name="close" size={18} /></button>
              </div>
              {search}{list}
            </div>
          </div>,
          getOverlayRoot()
        )}
      </>
    );
  }

export { Select };
